import { useSelector } from "react-redux";
import { ReadAcrossDataType } from "../page/IndividualReadAcrossPage"
import { selectNav } from "../../../store/selectors/nav.selector";

import StructureCell from "./HeatmapComponents/StructureCell";
import TruncatedText from "../../../components/Page/TruncatedText";
import { Skeleton, Tooltip } from "@mui/material";
import { ChangeEvent, useEffect, useState, Dispatch, SetStateAction } from "react";
import RADetailsModal from "../../../components/Popups/RADetailsModal";
import CategoryFilter from "./HeatmapComponents/CategoryFilter";
import { ReadAcross } from "../../../api/models";
import { filterOptions } from "../page/IndividualReadAcrossPage";


interface ReadAcrossDAGConf {
    model_type: string | null | undefined,
    readacross_type: string | null | undefined,
}

export default function RAHeatmap({
    readacrossMetadata,
    readacrossData,
    isLoading,
    selectedFilter,
    setSelectedFilter,
    searchInput,
    setSearchInput,
}: {
    readacrossMetadata: ReadAcross | null,
    readacrossData: ReadAcrossDataType[],
    isLoading: boolean,
    selectedFilter: string,
    setSelectedFilter: Dispatch<SetStateAction<string>>,
    searchInput: string,
    setSearchInput: Dispatch<SetStateAction<string>>,
}) {
    const isNavOpen = useSelector(selectNav);
    const [ filteredToxKeys, setFilteredToxKeys ] = useState<string[]>([]);

    const changeTableWidth = () => {
        if (isNavOpen) {
            return 'table-container-expanded';
        } else {
            return 'table-container';
        }
    }

    useEffect(() => {
        if (readacrossData.length > 0) {
            if (Object.keys(readacrossData[0].toxicology_data).length > 0) {
                const toxKeys = Object.keys(readacrossData[0].toxicology_data);
                if (toxKeys.length > 0) {
                    const filtered = toxKeys.filter(key => key.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()));
                    const categoricallyFiltered = filterToxKeys(selectedFilter, filtered, readacrossData);
                    setFilteredToxKeys(categoricallyFiltered);
                }
            } else {
                const toxKeys = Object.keys(readacrossData[0].reg_pred);
                if (toxKeys.length > 0) {
                    const filtered = toxKeys.filter(key => key.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()));
                    const categoricallyFiltered = filterToxKeys(selectedFilter, filtered, readacrossData);
                    setFilteredToxKeys(categoricallyFiltered);
                }
            }
        }
        
    }, [readacrossData, searchInput, selectedFilter]);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
    }

    const filterToxKeys = (filterKey: string, toxKeys: string[], raData: ReadAcrossDataType[]) => {
        let toxicKeys: string[] = [];

        if (filterKey === 'none') {
            toxicKeys = toxKeys
        } else if (filterKey === 'toxic-only') {
            if (Object.keys(raData[0].toxicology_data).length > 0) {
                toxKeys.forEach(key => {
                    const toxicCompounds = raData.filter(data => data.toxicology_data[key] === 1);
                    if (toxicCompounds.length > 0) {
                        toxicKeys.push(key);
                    }
                });
            } else if (Object.keys(raData[0].clf_proba).length > 0) {
                toxKeys.forEach(key => {
                    const toxicCompounds = raData.filter(data => data.clf_proba[key] === 1);
                    if (toxicCompounds.length > 0) {
                        toxicKeys.push(key);
                    }
                });
            }
        } else if (filterKey === 'data-only') {
            if (Object.keys(raData[0].clf_proba).length > 0) {
                toxKeys.forEach(key => {
                    const hasData = raData.some(data =>
                        data.clf_proba[key] !== undefined && data.clf_proba[key] !== null
                    );
                    if (hasData) {
                        toxicKeys.push(key);
                    }
                });
            } else {
                toxicKeys = toxKeys
            }
        }
        return toxicKeys;
    }



    return (
        <div className={`${changeTableWidth()} w-full transition-all grow overflow-y-auto overflow-x-auto bg-white rounded-xl border border-outline`}>
            <table className="w-full border-separate border-spacing-0">
                <thead>
                    {isLoading ? (
                        <tr className='bg-[#F5F7F7] text-default relative sticky top-0 z-30'>
                            {Array.from(Array(10).keys()).map(key => (
                                <td key={key} className="py-4 px-6"><Skeleton variant='text' width='100%' sx={{fontSize: '1rem'}}/></td>
                            ))}
                        </tr>
                    ) : (
                        <>
                            <tr className='bg-[#F5F7F7] text-default relative sticky top-0 z-30'>
                                <th className="sticky left-0 bg-[#F5F7F7] z-40 border-b border-r border-light-outline">
                                    <div className="py-4 px-6 flex justify-start items-center"></div>
                                </th>
                                {readacrossData.map((data, i) => (
                                    <th className="border-r border-b border-light-outline relative">
                                        <StructureCell data={data} index={i}/>  
                                        <RADetailsModal
                                            data={data}
                                            index={i}
                                            selectedFilter={selectedFilter}
                                            setSelectedFilter={setSelectedFilter}
                                            searchInput={searchInput}
                                            setSearchInput={setSearchInput}
                                        />
                                    </th>
                                ))}
                            </tr>
                            <tr className='bg-[#F5F7F7] text-default relative sticky top-[173px] z-30'>
                                <th className="sticky left-0 bg-[#F5F7F7] border-b border-r border-light-outline">
                                    <div className="py-2 px-6 flex justify-end items-center gap-3">
                                        <input 
                                            onChange={handleSearch}
                                            value={searchInput}
                                            className="grow rounded border border-light-outline text-[12px] px-2 py-1 text-default font-normal"
                                            placeholder="Search"
                                        />
                                        <CategoryFilter 
                                            options={filterOptions}
                                            selectedFilter={selectedFilter}
                                            setSelectedFilter={setSelectedFilter}
                                        />
                                    </div>
                                </th>
                                {readacrossData.map((data, i) => (
                                    <th className="border-r border-b border-light-outline"></th>
                                ))}
                            </tr>
                        </>
                    )}
                </thead>

                <tbody>
                    {isLoading ? (
                        <>
                        {Array.from(Array(10).keys()).map(k => (
                            <tr key={k}>
                                {Array.from(Array(6).keys()).map(key => (
                                    <td key={key} className="py-4 px-6"><Skeleton variant='text' width='100%' sx={{fontSize: '1rem'}}/></td>
                                ))}
                            </tr>
                        ))}
                        </>
                    ) : readacrossData.length > 0 && filteredToxKeys.length > 0 ? (
                        <>
                        {filteredToxKeys.map((toxKey, index) => (
                            <tr className={`bg-white`}>
                                <td className="sticky left-0 bg-white border-b border-r border-light-outline">
                                    <div className="py-2 px-6">
                                        <TruncatedText text={toxKey} alignment="left">
                                            <p className="text-default max-w-[150px] truncate">{toxKey}</p>
                                        </TruncatedText>
                                    </div>
                                </td>
                                {readacrossData.map((data, i) => {
                                    const toxData = data.toxicology_data;
                                    const dag_conf: ReadAcrossDAGConf= readacrossMetadata?.readacross_metadata?.airflow_dag_run_conf as ReadAcrossDAGConf;
                                    const isClassification = dag_conf?.model_type === 'classification';
                                    const units = dag_conf?.readacross_type === "toxref" ? " mg/kg/day" : null;
                                    if (toxData[toxKey] !== undefined) {
                                        const value = toxData[toxKey] as number | null;
                                        const displayValue = i === 0 ? 'Active' : isClassification ? 'Active' : `${value}${units ?? ""}`;
                                        return (
                                            <td className={`p-1 border-r border-b border-light-outline`}>
                                                <Tooltip title={!value ? 'No Effect/No Data' : displayValue}>
                                                    <div className={`${!value ? 'bg-[#7BC960]' : 'bg-warning'} h-[35px]`}></div>
                                                </Tooltip>
                                            </td>
                                        )
                                    } else {
                                        // NEW LOGIC FOR NEW DATA
                                        const clf_pred = data.clf_pred[toxKey] as number | null | undefined;
                                        const clf_proba = data.clf_proba[toxKey] as number | null | undefined;
                                        const reg_pred = data.reg_pred[toxKey] as number | null | undefined;
                                        const auc = data.auc[toxKey] as number | null | undefined;
                                        const pval = data.pval[toxKey] as number | null | undefined;
                                        const t0 = data.t0[toxKey] as number | null | undefined;
                                        const true_value = data.true_value[toxKey] as number | null | undefined;
                                        const confusion_label = data.confusion_label[toxKey] as string | null | undefined;
                                        const tanimoto = data.tanimoto as number | null | undefined;

                                        const showAgreementZero = clf_pred === 0;
                                        const showEverything = false;

                                        let colorClass = "bg-[#E0E0E0]"; // default grey
                                        if (i === 0) {
                                            if (showAgreementZero) {
                                                colorClass = "bg-[#7BC960]";
                                            } else if (clf_pred as number > 0) {
                                                colorClass = "bg-warning";
                                            }
                                        } else {
                                            if (reg_pred !== null && reg_pred !== undefined) {
                                                if (reg_pred === 0) colorClass = "bg-[#7BC960]";
                                                else if (reg_pred > 0) colorClass = "bg-warning";
                                            }
                                        }

                                        // Width for colored part
                                        const similarity = typeof tanimoto === "number" && tanimoto >= 0 && tanimoto <= 1 ? tanimoto : 0;
                                        const colorWidth = Math.round(similarity * 100);


                                        // Tooltip content for First Column
                                        const tooltipContent = (
                                            <div>
                                                <div>
                                                    {true_value !== null && true_value !== undefined ? (<div>
                                                        <div><b>True Value:</b> {true_value === null || true_value === undefined ? "N/A" : `${true_value}${units ?? ""}`}</div>
                                                    </div>) : (null)}
                                                    {confusion_label ? (<div>
                                                        <div><b>Confusion Label:</b> {confusion_label === null || confusion_label === undefined ? "N/A" : confusion_label}</div>
                                                    </div>) : (null)}
                                                    <div>
                                                        <b>Classification:</b>{" "}
                                                        {showAgreementZero ? "No Effect" : (clf_pred === null || clf_pred === undefined ? "N/A" : "Active")}
                                                    </div>
                                                    <div>
                                                        <b>ACT:</b>{" "}
                                                        {clf_proba === null || clf_proba === undefined ? "N/A" : Math.round(clf_proba * 1000) / 1000}
                                                    </div>
                                                    <div><b>AUC:</b> {auc === null || auc === undefined ? "N/A" : Math.round(auc * 1000) / 1000}</div>
                                                    <div><b>p-val:</b> {pval === null || pval === undefined ? "N/A" : Math.round(pval * 1000) / 1000}</div>
                                                </div>
                                                {showEverything ? (
                                                    <>
                                                        <div><b>t0:</b> {t0 === null || t0 === undefined ? "N/A" : Math.round(t0 * 1000) / 1000}</div>
                                                        <div><b>Predicted Value:</b> {reg_pred === null || reg_pred === undefined ? "N/A" : Math.round(reg_pred * 1000) / 1000}</div>
                                                    </>
                                                ) : null}
                                            </div>
                                        );

                                        const tooltipContent2 = (
                                            <div>
                                                {reg_pred === null || reg_pred === undefined ? "No Data" : (<div><b>True Value:</b> {reg_pred === null || reg_pred === undefined ? "N/A" : `${reg_pred}${units ?? ""}`}</div>)}
                                            </div>
                                        )

                                        return (
                                            <td className={`p-1 border-r border-b border-light-outline`}>
                                                <Tooltip title={i === 0 ? tooltipContent : tooltipContent2}>
                                                    <div className="h-[35px] w-full flex">
                                                        <div
                                                            className={`${colorClass}`}
                                                            style={{
                                                                width: `${colorWidth}%`,
                                                                minWidth: 0,
                                                                height: "100%",
                                                            }}
                                                        ></div>
                                                        <div
                                                            className="bg-white"
                                                            style={{
                                                                width: `${100 - colorWidth}%`,
                                                                minWidth: 0,
                                                                height: "100%",
                                                            }}
                                                        ></div>
                                                    </div>
                                                </Tooltip>
                                            </td>
                                        );
                                    }
                                })}
                            </tr>
                        ))}
                        </>
                    ) : (
                        <></>
                    )}
                </tbody>
            </table>
        </div>
    )
}